/**
 * Описание торгового сигнала.
 *
 * То, что человек собирает в конструкторе. Событие, которое из него
 * получается, — это `Signal`, и путать их нельзя: одно живёт в профиле
 * и правится, другое произошло и не переписывается. В интерфейсе оба
 * называются сигналом, потому что для трейдера это одно слово; в коде
 * они разведены жёстко.
 *
 * **Одна идея — один сигнал, даже если она работает в обе стороны.**
 * «Покупаю на выходе из перепроданности, продаю на выходе из
 * перекупленности» — это одна мысль, и заводить под неё два описания
 * значит обречь человека править их парой и однажды забыть про второе.
 * Поэтому у сигнала не направление, а ветви: у каждой свои условия,
 * потому что условия покупки и продажи почти никогда не зеркальны
 * настолько, чтобы вывести одно из другого.
 *
 * Устройство ветви — три части, и у каждой своё поведение:
 *
 *   Контекст        фон рынка: должен быть верен в момент срабатывания
 *   Триггер         событие: без него сигнала нет
 *   Подтверждения   усиливают, каждое со своим весом
 *
 * Контекст и триггер — ворота: не выполнились, сигнала нет вовсе.
 * Подтверждения на факт срабатывания не влияют, они определяют силу.
 * Разница существенная: «RSI пересёк тридцать» — событие, случается в
 * одну свечу; «цена выше EMA 200» — состояние, верное неделями. Смешать
 * их в один список «и» — значит получить сигнал, который возникает на
 * каждом баре растущего тренда.
 *
 * **Score — процент набранного веса подтверждений.** Триггер в вес не
 * входит: он ворота, а не слагаемое. Подтверждений нет — Score равен
 * ста: ничему не дано было провалиться, и сигнал ровно так же силён,
 * как его триггер. Это же поведение было у правил до появления весов,
 * поэтому старые описания продолжают работать без единой правки.
 */
import { groupOf, isGroup, } from './rule.js';
/** Пустая ветвь заданного направления. */
export function emptyBranch(direction) {
    return { direction, context: [], trigger: null, confirmations: [] };
}
/** Ветвь этого направления, если она есть. */
export function branchOf(spec, direction) {
    return spec.branches.find((branch) => branch.direction === direction) ?? null;
}
/** Направления, которые сигнал ищет. */
export function directionsOf(spec) {
    return spec.branches.map((branch) => branch.direction);
}
/** Ушла ли ветвь в расширенный режим. */
export function isAdvancedBranch(branch) {
    return branch.advanced !== undefined;
}
/** Есть ли у ветви главное событие. Пусто — старое описание. */
export function isEventBranch(branch) {
    return branch.trigger !== null;
}
/**
 * Ворота ветви: то, что обязано выполниться, чтобы сигнал возник.
 *
 * Контекст и триггер, соединённые «и». Подтверждения сюда не входят —
 * они меняют силу, а не факт.
 */
export function gateOf(branch) {
    if (branch.advanced)
        return branch.advanced;
    const items = [...branch.context];
    if (branch.trigger)
        items.push(branch.trigger);
    return { kind: 'group', join: 'and', items };
}
/**
 * Можно ли показать расширенную группу тремя карточками.
 *
 * Обратная сторона `gateOf`: та сворачивает «фон плюс событие» в
 * плоское «и», эта разворачивает обратно. Получается не всегда —
 * вложенное «или» тремя карточками не изобразить, — и тогда честный
 * ответ `null`, а не приблизительный.
 *
 * Порядок восстанавливается тот же, каким сворачивали: событие
 * последнее, всё до него — фон. Иначе после возврата человек увидел бы
 * свои условия перетасованными и решил, что что-то потерялось.
 */
export function simpleAgain(group) {
    if (group.join !== 'and')
        return null;
    const conditions = [];
    for (const item of group.items) {
        if (isGroup(item))
            return null;
        conditions.push(item);
    }
    const trigger = conditions.pop() ?? null;
    return { context: conditions, trigger };
}
/** Сумма весов подтверждений ветви. */
export function totalWeight(branch) {
    let total = 0;
    for (const confirmation of branch.confirmations) {
        if (confirmation.weight > 0)
            total += confirmation.weight;
    }
    return total;
}
/**
 * Score по набранному весу.
 *
 * Целое от нуля до ста — таким его требует контракт события. Нечего
 * набирать (подтверждений нет или все веса нулевые) — сто: сигнал
 * ровно так же силён, как его триггер, и притворяться слабым ему не с
 * чего.
 */
export function scoreOf(gained, total) {
    if (total <= 0)
        return 100;
    const share = Math.round((gained / total) * 100);
    return Math.max(0, Math.min(100, share));
}
/**
 * Отпечаток описания: всё, что влияет на результат.
 *
 * По нему решается, поднимать ли версию. Переименование сигнала или
 * включение уведомлений результата не меняют и версию не двигают —
 * иначе каждое включение галочки объявляло бы прошлые события чужими.
 */
export function specFingerprint(spec) {
    return JSON.stringify([
        spec.venue,
        spec.symbol,
        spec.timeframe,
        spec.threshold,
        spec.branches.map((branch) => [
            branch.direction,
            branch.context,
            branch.trigger,
            branch.confirmations.map((entry) => [entry.condition, entry.weight]),
            branch.advanced ?? null,
        ]),
    ]);
}
/* ------------------------------------------------- совместимость с Rule */
/**
 * Ветвь в том виде, в каком её понимает вычислитель.
 *
 * Отдельного движка у сигналов нет и не будет: и график, и Core, и
 * проверка на истории считают одно и то же одним и тем же кодом. Второй
 * вычислитель означал бы расхождение «на графике стрелка, а бот не
 * купил» — ровно то, ради чего вся эта архитектура и построена.
 */
export function ruleFromBranch(spec, branch) {
    return {
        id: `${spec.id}@${branch.direction}`,
        name: spec.name,
        venue: spec.venue,
        symbol: spec.symbol,
        timeframe: spec.timeframe,
        when: gateOf(branch),
        action: branch.direction === 'LONG' ? 'long' : branch.direction === 'SHORT' ? 'short' : 'alert',
        version: spec.version,
        enabled: spec.enabled,
        notify: spec.notify,
        createdAt: spec.createdAt,
        updatedAt: spec.updatedAt,
    };
}
/**
 * Старое правило как описание сигнала.
 *
 * Всё дерево уходит в контекст, а триггер остаётся пустым, и это не
 * упущение. У правила главного события не было: оно срабатывало, пока
 * условия верны. Назначить триггером одно из них — значит соврать о
 * том, что человек имел в виду, и изменить смысл его описания задним
 * числом. Правило продолжает работать ровно как работало, а конструктор
 * предложит выбрать событие, когда человек его откроет.
 *
 * Веса тоже пустые: подтверждений у правила не было, и Score выходит
 * сто — то самое значение, которое правила ставили всегда.
 */
export function specFromRule(rule) {
    const group = groupOf(rule);
    return {
        id: rule.id,
        name: rule.name,
        venue: rule.venue,
        symbol: rule.symbol,
        timeframe: rule.timeframe,
        branches: [
            {
                direction: rule.action === 'long' ? 'LONG' : rule.action === 'short' ? 'SHORT' : 'ALERT',
                /*
                 * Верхнее «и» разбирается на отдельные карточки, а «или» ложится
                 * целиком одной. Разложить «или» по карточкам нельзя: они
                 * читаются как «и это, и это», а там было «либо».
                 */
                context: group.join === 'and' ? [...group.items] : [group],
                trigger: null,
                confirmations: [],
            },
        ],
        threshold: 0,
        version: rule.version,
        enabled: rule.enabled,
        notify: rule.notify,
        createdAt: rule.createdAt,
        updatedAt: rule.updatedAt,
    };
}
//# sourceMappingURL=signal-spec.js.map